import { DataTable } from "primereact/datatable";
import { useEffect, useState, Suspense, useRef } from "react";
import { ColumnDef } from "../../models/orders/order-list-model";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Paginator } from "primereact/paginator";
import AppLoading from '../app-loading/app-loading';
import orderServices from "../../services/orders/orderservice";
import { Toast } from 'primereact/toast';


interface DataTableProps {
  dateFilter: any;
  onDataLoaded?: (data: any[]) => void;
}

const OnlinePaymentDataTable: React.FC<DataTableProps> = (props) => {

  const [orderDataList, setOrderDataList] = useState<any>();
  const [loading, setLoading] = useState(false);
  const toast = useRef<Toast>(null);
  const { dateFilter, onDataLoaded } = props;
  const startDate = dateFilter?.startDate;
  const endDate = dateFilter?.endDate;
  const userId = dateFilter?.userId;
  const collectionType = dateFilter?.collectionType;

  // Paginator state
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  const onPageChange = (event: any) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  const pagedData = orderDataList ? orderDataList.slice(first, first + rows) : [];

  useEffect(() => {
    if (!startDate && !endDate) return;
    setLoading(true);
    orderServices.cashandOnlineList({ dateFilter: { startDate, endDate, userId, collectionType } }).then((response: any) => {
      if (response.length > 0) {
        setOrderDataList(response.collectionList);
        onDataLoaded?.(response.collectionList ?? []);
        setLoading(false)
      }
      else {
        setOrderDataList(response.collectionList);
        onDataLoaded?.(response.collectionList ?? []);
        setLoading(false)
      }
    });
  }, [startDate, endDate, userId, collectionType]);


  const columns: ColumnDef[] = [
    {
      field: "orderID",
      header: "Order #",
      style: { minWidth: "5rem" },
      sortable: true,
    },
    {
      field: "invoiceNo",
      header: "Invoice #",
      style: { minWidth: "5rem" },
      sortable: true,
    },
    {
      field: "invoiceDate",
      header: "Invoice Date",
      style: { minWidth: "9rem" },
      sortable: true,
      cell: (row: any) =>
        new Date(row.invoiceDate).toLocaleDateString("en-gb", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      field: "invoiceBy",
      header: "Invoice By",
      style: { minWidth: "9rem" },
      sortable: true,
    },
    {
      field: "customerName",
      header: "Customer",
      style: { minWidth: "10rem" },
      sortable: true,
    },
    {
      field: "primaryContactNo",
      header: "Ph No",
      style: { minWidth: "8rem" },
      sortable: true,
    },
    {
      field: "courierCharges",
      header: "Courier Charges",
      style: { minWidth: "5rem" },
      sortable: true,
      cell: (row: any) =>
        Math.round(row.courierCharges).toLocaleString("en-gb", {
          style: "currency",
          currency: "INR",
        }),
    },
    {
      field: "handlingCharges",
      header: "Handling Charges",
      style: { minWidth: "5rem" },
      sortable: true,
      cell: (row: any) =>
        Math.round(row.handlingCharges).toLocaleString("en-gb", {
          style: "currency",
          currency: "INR",
        }),
    },
    {
      field: "paymentGatewayCharges",
      header: "PaymentGateway Charges",
      style: { minWidth: "5rem" },
      sortable: true,
      cell: (row: any) =>
        Math.round(row.paymentGatewayCharges).toLocaleString("en-gb", {
          style: "currency",
          currency: "INR",
        }),
    },
    {
      field: "discountAmount",
      header: "Discount Amount",
      style: { minWidth: "5rem" },
      sortable: true,
      cell: (row: any) =>
        Math.round(row.discountAmount).toLocaleString("en-gb", {
          style: "currency",
          currency: "INR",
        }),
    },
    {
      field: "grossAmount",
      header: "Gross Amount",
      style: { minWidth: "5rem" },
      sortable: true,
      cell: (row: any) =>
        Math.round(row.grossAmount).toLocaleString("en-gb", {
          style: "currency",
          currency: "INR",
        }),
    },
    {
      field: "rewardAmount",
      header: "Reward Amount",
      style: { minWidth: "5rem" },
      sortable: true,
      cell: (row: any) =>
        Math.round(row.rewardAmount).toLocaleString("en-gb", {
          style: "currency",
          currency: "INR",
        }),
    },
    {
      field: "finalAmount",
      header: "Final Amount",
      style: { minWidth: "5rem" },
      sortable: true,
      cell: (row: any) =>
        Math.round(row.finalAmount).toLocaleString("en-gb", {
          style: "currency",
          currency: "INR",
        }),
    },
  ];


  return (
    <>
      <Suspense fallback={<AppLoading />}>
        <Toast ref={toast} />
        <div className="col-12 card border-noround p-2" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

          {/* Horizontally scrollable table area */}
          <div style={{ overflowX: 'auto', overflowY: 'auto', flex: 1, minHeight: 0 }}>
            <DataTable
              value={pagedData}
              stripedRows
              showGridlines
              sortMode="multiple"
              emptyMessage="No Record found."
            >
              {columns.map((col, i) => (
                <Column
                  key={i}
                  field={col.field}
                  header={col.header}
                  sortable={col.sortable}
                  style={col.style}
                  body={col.cell}
                />
              ))}
            </DataTable>
          </div>

          {/* Sticky paginator footer */}
          <div style={{
            position: 'sticky',
            bottom: 0,
            background: 'white',
            borderTop: '1px solid #e5e7eb',
            zIndex: 10,
            flexShrink: 0,
          }}>
            <Paginator
              first={first}
              rows={rows}
              totalRecords={orderDataList ? orderDataList.length : 0}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              onPageChange={onPageChange}
              template="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
              currentPageReportTemplate="{first} to {last} of {totalRecords}"
            />
          </div>

        </div>
        {loading && <AppLoading />}
      </Suspense>
    </>
  );
};

export default OnlinePaymentDataTable;